import type { Plugin, Ref } from 'vue'
import type { Maybe, Response } from '@/types'

import { ref } from 'vue'

import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useCustomerAuthStore } from '@/stores/customer/auth'

import { isEmpty, prepareQueryParams } from '@/helpers'

interface ApiPluginOptions {
  config?: { baseURL?: string }
  env?: 'development' | 'production' | 'staging'
}

class Instance {
  options: {
    baseURL?: string
    env?: 'development' | 'production' | 'staging'
    headers: Record<string, any>
  } = {
    baseURL: undefined,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/plain, */*',
    },
  }

  async get(url: string, config?: { params: Record<string, any> }) {
    let input = `${this.options.baseURL}${url}`

    if (!isEmpty(config?.params ?? {})) {
      input = `${input}?${prepareQueryParams(config?.params ?? {})}`
    }

    const response = await fetch(input, {
      method: 'GET',
      headers: this.options.headers,
    })

    const result = await response.json()

    const wrapper = {
      data: result,
      status: response.status,
      statusText: response.statusText,
    }

    await handleError(wrapper, this.options)

    return Promise.resolve(wrapper)
  }
  async post(
    url: string,
    body: RequestInit['body'],
    config?: {
      headers: Instance['options']['headers']
      formData?: boolean
      params?: Record<string, any>
    }
  ) {
    let input = `${this.options.baseURL}${url}`

    if (!isEmpty(config?.params ?? {})) {
      input = `${input}?${prepareQueryParams(config?.params ?? {})}`
    }

    const init = {
      method: 'POST',
      headers: this.options.headers,
      body: config?.formData ? body : JSON.stringify(body),
    }

    if (config) init.headers = { ...init.headers, ...config.headers }

    if (config?.formData) {
      delete init.headers['Content-Type']
    }

    const response = await fetch(input, init)

    const result = await response.json()

    const wrapper: Response<any, any> = {
      data: result,
      status: response.status,
      statusText: response.statusText,
    }

    await handleError(wrapper, this.options)

    return Promise.resolve(wrapper)
  }
  async put(
    url: string,
    body: RequestInit['body'],
    config?: { params: Record<string, any>; formData?: boolean }
  ) {
    let input = `${this.options.baseURL}${url}`

    if (!isEmpty(config?.params ?? {})) {
      input = `${input}?${prepareQueryParams(config?.params ?? {})}`
    }

    const init = {
      method: 'PUT',
      headers: this.options.headers,
      body: config?.formData ? body : JSON.stringify(body),
    }

    if (config?.formData) {
      delete init.headers['Content-Type']
    }

    const response = await fetch(input, init)

    const result = await response.json()

    const wrapper: Response<any, any> = {
      data: result,
      status: response.status,
      statusText: response.statusText,
    }

    await handleError(wrapper, this.options)

    return Promise.resolve(wrapper)
  }
  async delete(url: string, config?: { params: Record<string, any> }) {
    let input = `${this.options.baseURL}${url}`

    if (!isEmpty(config?.params ?? {})) {
      input = `${input}?${prepareQueryParams(config?.params ?? {})}`
    }

    const response = await fetch(input, {
      method: 'DELETE',
      headers: this.options.headers,
    })

    const result = await response.json()

    const wrapper: Response<any, any> = {
      data: result,
      status: response.status,
      statusText: response.statusText,
    }

    await handleError(wrapper, this.options)

    return Promise.resolve(wrapper)
  }

  constructor(options?: ApiPluginOptions) {
    Object.assign(this.options, {
      baseURL: options?.config?.baseURL,
      env: options?.env,
    })
  }
}

const rest = ref({}) as Ref<Instance>

export const vueApi: Plugin = {
  install(app, options: ApiPluginOptions) {
    rest.value = new Instance(options)
  },
}

export const useApi = () => {
  return { rest, setToken }
}

function setToken(token?: Maybe<string>) {
  if (!rest.value.options) return

  if (token) {
    rest.value.options.headers.Authorization = `Bearer ${token}`
  } else {
    rest.value.options.headers.Authorization = undefined
  }
}

function handleError(wrapper: any, options: ApiPluginOptions) {
  const status: number = wrapper?.status
  const otherError =
    wrapper?.data?.errors?.other ??
    (status === 404 ? 'Ошибка: не найдено!' : 'Неизвестная ошибка!')

  function checkIfManager() {
    return /manager/.test(
      (useUiStore()?.router.currentRoute.name ?? '') as string
    )
  }

  const errors: Record<number, any> = {
    401: () =>
      checkIfManager()
        ? useAuthStore()?.logout()
        : useCustomerAuthStore()?.logout(),
    403: () => useUiStore()?.showToast({ text: 'У вас недостаточно прав!' }),
    404: () => useUiStore()?.showToast({ text: otherError }),
    418: () => useUiStore()?.showToast({ text: otherError }),
  }

  if (!errors[status]) return Promise.resolve()

  if (options.env === 'development') console.debug('[API/status]', status)

  if (checkIfManager()) errors[status]?.()

  return Promise.reject(wrapper)
}
